import React, {Fragment, isValidElement, useEffect, useRef, useState} from "react";
import {abvNum, abvNumEx, funcOrValOr, getAgo, modDate, objToArrK} from "../tools/CommonJS";
import {classNameScrollbar, classNameScrollbarThin, dispDateTime, setClassNameScrollbar, timePickerFrmt} from "../tools/CommonHtml";
import {Button, FormLabel, TextInput} from "../tools/Inputs";
import {AnimatePickerRotation, icn} from "../tools/UIIcons";
import {ToggleDiv} from "../tools/UIAnimateToggleDiv";
import {flexRow, tailCheck} from "../tools/CommonReact";
import {Badge, TextEllip} from "../tools/UIBlocks";
import {genTooltip} from "./AppContext";
import {ReadMorePopUpWrapper} from "../tools/UIPopMenu";
import {FormHint, FormHintHover} from "./CommonUIPopups";
import {UIComboBox} from "../tools/UIComboBox";
import {StatusPill} from "./UIDataComponents.jsx";

const classNameBaseScrollBar = "scrollbar scrollbar-thumb-badge/75 hover:scrollbar-thumb-badge scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-track-bg2";
setClassNameScrollbar(`${classNameBaseScrollBar} scrollbar-w-3`, `${classNameBaseScrollBar} scrollbar-thin`);

const SIZES = {xs: "w-1.5 h-1.5", sm: "w-2 h-2", md: "w-3 h-3"};
const TYPES = {default: "bg-prm-light", warning: "bg-warning", success: "bg-success", handle: "bg-handle", threat: "bg-threat", sec: "bg-sec/60"};

export const Title = ({className = "", classNameColor = "", tip = null, tipClick = false, ...props}) => !tip ?
    <TextEllip data-test="title" className={`${className} ${classNameColor || "text-title"}`} {...props}/>
    :
    <span className="relative group flex w-full overflow-hidden">
        <TextEllip data-test="title" className={`${className} ${classNameColor || "text-title"}`} {...props}/>
        {React.createElement(tipClick ? FormHint : FormHintHover, {
            className: `group-hover:opacity-100 ${tipClick ? "pl-1 pt-[0.18rem] opacity-20 text-2xs icon-question-mark" : "absolute -right-1 translate-x-full top-1/2 -translate-y-1/2 group-hover:icon-question opacity-0"}`,
            tip
        })}
    </span>;

export const DashCard = ({className= "", ...props}) =>
    <div className={tailCheck(className, "rounded-lg", "bg-bg2", "shadow-card", "overflow-hidden")} {...props}/>;

export const Divider = ({className= "", classNameColor="", vertical = false, ...props}) =>
    <div className={`shrink-0 ${vertical ? "w-[2px] self-stretch" : "h-[2px]"} ${classNameColor || "bg-divider"} ${className}`} {...props}/>;

export const DividerListButtons = ({className = "", classNameColor = "bg-bg3", ...props}) =>
    <div className={`shrink-0 w-[2px] h-5 ${classNameColor} ${className}`} {...props}/>;

export const DashCardDivider = ({className}) =>
    <div className={`h-[2px] dark:bg-bg3 ${className}`}/>;

export const GridDivider = ({className, style}) =>
    <div style={{gridColumn: "1/-1", ...style}} className={`bg-bg1 h-[2px] ${className}`}/>;

export const DevicesSpreadInfo = ({className, h, sum, subtitle, classNameValue}) =>
    <div data-test={`devices-spread-info-${h.toLowerCase().toKebabCase()}`} className={tailCheck(className, "gap-2", "flex flex-col", "text-title/80")}>
        <Title data-test="dsi-title" classNameColor="text-xs text-gray-300">{h}</Title>
        <Title data-test="dsi-value" classNameColor={`text-lg ${classNameValue}`}>{typeof sum === "number"? sum.toLocaleString() : sum}</Title>
        <Title data-test="dsi-subtitle" classNameColor="text-xs grow flex self-baseline text-subtitle1">{subtitle}</Title>
    </div>;

export const BulletItem = ({Tag = Button, children, className="", classNameBullet="",  ...props}) =>
    <Tag  data-test="bullet-item" className={`flex items-center gap-4 ${className}`} {...props}>
        <div className={tailCheck(classNameBullet, "aspect-square", "w-0.5", "shrink-0", "min-w-0", "rounded-full", "bg-prm-main")}/>
        {children}
    </Tag>;

export const DotIndicator = ({size = "sm", type = "default", className = "", children, ...props}) => (
    <div className={`${SIZES[size] || SIZES.sm} ${TYPES[type] || TYPES.default} rounded-full ${className}`} {...props}>
        {children}
    </div>
);

export const ListItemTitleSub = ({title, subtitle, className ="", classNameTitle ="", classNameSub ="", tip ="", tipSub ="", ...props}) =>
    <div data-test="list-item-title-sub" className={tailCheck(className, "flex flex-col", "gap-0.5 min-w-0")} {...props}>
        {typeof title === "string"? <Title classNameColor={`text-sm ${classNameTitle || "text-title"}`} tip={tip}>{title}</Title> : title}
        {!!subtitle && <Title data-test="subtitle" className={`text-xs font-sans ${classNameSub}`} tip={tipSub} classNameColor="text-tx-cool-grey">{subtitle}</Title>}
    </div>;

export const ListItem3Titles = ({titles, className, classNameTitles, tips, ...props}) =>
    <div className={tailCheck(className, "flex flex-col", "min-w-0")} {...props}>
        {titles.map((title, it) => <Title key={it} classNameColor={classNameTitles?.[it]} tip={tips?.[it]}>{title}</Title>)}
    </div>;

export const TitleAbvNum = ({val, className = undefined, dataTest = undefined, tipFmt = undefined, ...props}) =>
    <span
        data-test={dataTest} {...props} className={`text-nowrap overflow-hidden ${tailCheck(className || "text-title", "w-full")}`}
        {...tipFmt === false? {} : tipFmt? genTooltip(tipFmt.format(val.toLocaleString())) :
            {title: typeof val === "number" && val >= 1000? val.toLocaleString() : undefined}}>
        {typeof val === "number" ? abvNumEx(val) : val || <>&nbsp;</>}
    </span>;


export const WarningMsg = ({text, title}) =>
    <div className="flex flex-col gap-3" data-test="warning-msg">
        {flexRow("gap-2",
                 <div className="text-2xl text-warning icon-warning mr-1"/>,
                 <Title className="text-xl">{title}</Title>)}
        <div className="text-h2">
            {text}
            <p className="mt-2">Are you sure you want to proceed?</p>
        </div>
    </div>;

export const ListItemValue = ({h, val, val2 = undefined, className = "", classNameH = "", classNameVal = "", classNameVal2 = "", tip = undefined, tipVal = undefined, tipVal2 = undefined, dataTest = undefined, ...props}) =>
    <div
        data-test="list-item" className={tailCheck(className,
                                                   "flex relative", "shrink-0", "min-w-0", "flex-col", tip? "group overflow-visible" : "overflow-hidden")} {...props}>
        <div data-test="list-item-value" className={`flex gap-1`}>
            {(val || val === 0) && <TitleAbvNum dataTest={`${dataTest ?? h?.toLowerCase?.()}-list-item1`} val={val} className={classNameVal} tipFmt={tipVal}/>}
            {(val || val === 0) && (val2 || val2 === 0) && "/"}
            {(val2 || val2 === 0) && <TitleAbvNum dataTest={`${h?.toLowerCase?.()}-list-item2`} val={val2} className={classNameVal2}/>}
        </div>
        {((val || val === 0) || (val2 || val2 === 0)) &&
            <Title data-test="list-item-title" classNameColor={`text-tx-steel text-xs ${classNameH}`} tip={tip}>{h}</Title>}
    </div>;

export const ListItemValues = ({h, className, classNameH ="", vals, classNameVals ="", ...props}) =>
    <div data-test="list-item" className={tailCheck(className, "flex", "shrink-0", "min-w-0", "overflow-hidden", "flex-col")} {...props}>
        {!!vals?.length && <span data-test="list-item-value" className={`whitespace-pre ${classNameVals}`}>{vals}</span>}
        {!!vals?.length &&
            <Title data-test="list-item-title" classNameColor={`text-tx-steel text-xs ${classNameH}`}>{h}</Title>}
    </div>;

export const TopPageWarning = ({text}) =>
    <div className="bg-warning/10 rounded-lg flex items-center py-2 pl-8 pr-4 gap-4 shrink-0 min-w-0">
        <div className="w-6 fill-warning">{icn.warn}</div>
        <div className="text-h2">{text}</div>
    </div>;

// use Tagged-Template literal to format the vals input of ListItemValues.
// e.g.: listFormatValues`${row.numComputers - row.numToUpdate} / ${row.numComputers} - ${(1 - row.numToUpdate / row.numComputers) *100 |0}%`
// a value can be an object if necessary to specify className or dataTest: {val, className, dataTest}
//
export function listFormatValues(strs, ...vals) {
    return strs.map((s, is) => {
        let val = vals[is], className, dataTest, tipFmt, onClick;
        if (typeof val === "object" && val !== null)
            ({val, className, dataTest, tipFmt, onClick} = val);

        return [
            ...s && (val !== undefined || is === vals.length) && (!is || vals[is-1] !== undefined)? [s] : [],
            ...val !== undefined? [<TitleAbvNum key={is} {...{val, onClick, className, tipFmt, dataTest: dataTest ? dataTest : `list-val${is+1}`}}/>] : []
        ];
    }).flat();
}

// Used to display a list of values in a row of a table for ListColDef.
export const makePills = genProps =>
    ({value, rowData, ...addProps}) => <>
        {value?.map(v => {
            const {value, className, ...props} = genProps?.({value: v, rowData, ...addProps}) ?? {value: v};
            return <div key={v} className={`rounded-full ${className ?? "bg-desc/70"} text-xs px-2 py-0`} {...props}>{value}</div>;
        }) ?? " "}
    </>;

// Like makePills but wraps in ReadMorePopUpWrapper with autoFit — pills that don't fit collapse into a +N popup.
export const makePillsAutoFit = (genProps, {classNameBtn, ...wrapperProps} = {}, emptyVal = " ") =>
    ({value, rowData, ...addProps}) => value?.length ?
        <ReadMorePopUpWrapper
            autoFit
            classNameBtn={tailCheck(classNameBtn, "text-xs", "text-desc", "rounded-full", "bg-bg6", "border-", "shadow-", "px-2", "py-0")}
            {...wrapperProps}>
            {value.map(v => {
                const {value, className, ...props} = genProps?.({value: v, rowData, ...addProps}) ?? {value: v};
                return <div key={v} className={tailCheck(className, "rounded-full", "bg-desc/70", "text-xs", "px-2", "py-0")} {...props}>{value}</div>;
            })}
        </ReadMorePopUpWrapper> : emptyVal;

export const TitleWithBadges = ({h, badges, className, dataTest}) =>
    <div className={`flex items-center gap-2 ${className}`}>
        <Title data-test={`${dataTest}-title`} className="grow" classNameColor=" ">{h}</Title>
        {badges?.map(({v, fTip, className}, i) =>
            <Badge
                data-test={`${dataTest}-badge-${i}`} key={i} className={tailCheck(className, "text-2xs text-bg1", "bg-icon", "px-1", "leading-3")} pill
                title={fTip?.format(v.toLocaleString?.() ?? v) ?? (v > 999? v.toLocaleString() : undefined)}>
                {typeof v === "number" ? abvNum(v) : v}
            </Badge>)}
    </div>;


export const genStatusPills = ({tpc, rowTasks = {}, navToBasic, onChange = undefined, severity}) =>
    ({h, getVal, noVal, exParams, isTask, tip, className, onClick}) => {
        const val = getVal(isTask ? rowTasks : tpc);
        h = funcOrValOr(h, val);
        return <StatusPill
            key={h} h={h} val={!noVal && val}
            onClick={e => {
                onClick && onClick?.(onChange);
                exParams && navToBasic(exParams)(e);
            }}
            {...(tip && {tip: funcOrValOr(tip, val)})}
            className={funcOrValOr(className, severity)}/>;
    };

export const LastUserSpan = ({lastUser, comma = false}) =>
    lastUser && <span>
        {comma && ", "}
        Last user - <span className="text-desc">{lastUser}</span>
    </span>;

export function InfoPill({h, className, ...props}) {
    return <TextEllip {...props} className={tailCheck(className, "rounded-full", "font-thin", "text-sm text-desc")}>
        {h}
    </TextEllip>;
}

// type: ({val: number; className?: string, classNameDot?: string})[]
export const dispDotVals = ({dots, className = ""}) =>
    <div className={tailCheck(className, "flex items-center", "gap-4")}>
        {dots.map(({val, className, classNameDot, tipFmt, tip, c}, i) =>
            <div key={i} className={`flex items-center gap-1 text-xs ${className}`} {...(tip && genTooltip(tip))}>
                <div className={`rounded-full w-1.5 h-1.5 shrink-0 min-w-0 ${classNameDot}`} {...(c && {style: {backgroundColor: c}})}/>
                <TitleAbvNum {...{val, tipFmt}}/>
            </div>)}
    </div>;

export function FilterPills({onChange, value, field, opts, className}) {
    return <div className={tailCheck(className, "flex items-center gap-1 flex-wrap", "text-xs")} data-test="filter-pills-container">
        {opts.map((opt, i) =>
            <Button
                key={opt.k || i} data-test={`list-item-${opt.k}`}
                className={`${value === opt.k ? "bg-prm-main text-prm-contrast" : "bg-bg1 opacity-80 hover:opacity-100"} px-2 py-0.5 transition-all rounded-md`}
                onClick={() => onChange(field, opt.k)} {...(opt.tip && genTooltip(opt.tip))}>
                {opt.h}
            </Button>)}
    </div>;
}

export const RemediationDispAcc = ({repoRemedies}) =>
    <div className="pl-3 flex flex-col gap-2">
        {Object.entries(repoRemedies).map(([k, r]) =>
            <AccordionDiv key={k} summary={r.title} arrow classNameSummary="text-desc" classNameDiv="mt-0 whitespace-pre-wrap" className="shadow-none">
                <div>{r.desc}</div>
                <div>{r.alt}</div>
            </AccordionDiv>)}
    </div>;

export const PanelItem = ({h, val, className, classNameVal, ...props}) =>
    <div className={tailCheck(className, "flex min-w-0", "overflow-hidden", "flex-col")} {...props}>
        {(val ?? false) !== false && <>             {/* undefined or null or false */}
            <Title classNameColor={`text-tx-steel text-xs ${typeof val === "number" && "text-center"}`}>
                {h}
            </Title>
            <TitleAbvNum val={val} className={classNameVal}/>
        </>}
    </div>;

export const getUserIfExt = (user) => (user?.userType === "External" ? user?.user?.after("_") : user?.user ?? user);

export const dispUserName = user => user?.userName || (getUserIfExt(user))?.split?.("\\").last.before("@");
export const dispUserDesc = user => user?.userName? `${user?.userName} (${getUserIfExt(user)})` : getUserIfExt(user);

export const LetterAvatar = ({user, className = "", noTip = false, tip, tipOpts, ...props}) => {
    const isRight = (tipOpts?.align ?? "bottom-right").includes("right");
    const opts = {align: "bottom-right", margin: {x: isRight ? -7 : 5, y: 10}, ...tipOpts};
    return <div
        data-test="letter-avatar"
        className={tailCheck(className, "h-7", "text-xs", "bg-[#6c7987]", "flex items-center justify-center aspect-square rounded-full text-white")}
        {...(!noTip && genTooltip(tip === undefined ? dispUserDesc(user) : tip, isRight ? "right" : "left", opts))} {...props}>
        {dispUserName(user)?.split(/[ _.]/).filter((_, i, a) => !i || i === a.length - 1).map(w => w[0]).join("").toUpperCase()}
    </div>;
};

export function DispAgoAndTipDateTime({tm, className = undefined, opts = undefined}) {
    tm = tm && tm !== "0001-01-01T00:00:01Z"? new Date(tm) : undefined;
    return <TitleAbvNum val={tm? getAgo(tm, opts) : "N/A"} tipFmt={tm && dispDateTime(tm)} className={tailCheck(className, "text-")}/>;
}

export const ListItemScanTime = ({h = undefined, tm, className}) =>
    <ListItemValue h={h ?? "Scanned"} val={<DispAgoAndTipDateTime tm={tm}/>} className={tailCheck(className, "text-xs items-end text-end", "min-w-10", "gap-1")}/>;

export const FilterEntry = ({title, children, className, classNameColor, info, tip, field, disabled, testId}) =>
    !title && !testId ? children :
        <div data-test={testId ?? `${title?.toLowerCase?.().toKebabCase()}-filter`} data-field={field} className={tailCheck(className, "flex flex-col", "gap-2")}>
            <div className="flex items-center gap-3">
                <Title tipClick classNameColor={tailCheck(classNameColor, "text-sm", disabled && "opacity-50")} tip={tip} data-test="filter-entry-title">
                    {title}</Title>
                {info}
            </div>
            {children}
        </div>;

export const FilterEntryHorizontal = ({title, children, className}) =>
    <div className={`flex items-center justify-end gap-4 ${className}`}>
        <Title className="text-xs text-tx-cool-grey">{title}</Title>
        {children}
    </div>;

export const InfoCell = ({className ="", title, children, linesToShow = 4, bottomLink = undefined}) =>
    <div data-test={`${title.toLowerCase().toKebabCase()}-info-cell`} className={`text-xs flex flex-col ${className}`}>
        <Title data-test='info-cell-title' className="pb-0.5 text-xs font-bold" classNameColor="text-desc" children={title}/>
        <ReadMoreParagraph className="text-h2" {...{linesToShow, dataTest: "info-cell-value"}}>
            {children}
        </ReadMoreParagraph>
        {bottomLink}
    </div>;

export const TableCard = ({filter = undefined, top = undefined, main, reset = undefined, filterTitle = undefined, topTitle = undefined, topDivider = undefined,
    classNameCard = undefined, classNameFilter = undefined, classNameMain = undefined}) =>
    <DashCard
        className={tailCheck(classNameCard, "grow min-w-0 min-h-[25rem] flex", "p-8", "gap-8", "h-full", classNameMain?.includes("min-w-") && "min-w-min")}>
        {filter && <div className={tailCheck(classNameFilter,
                                             "w-68", "shrink-0", "h-full", "flex flex-col", "max-sm:w-36", "items-stretch", "gap-7", "resize-x overflow-x-hidden")}>
            <div className="pr-2 flex gap-6 items-baseline justify-between">
                <Title className="font-bold text-xl">{filterTitle ?? "Filter"}</Title>
                {reset && <Button data-test="reset-button" onClick={reset} className="text-sm text-prm-light">Reset</Button>}
            </div>
            <Divider classNameColor="mr-2 bg-bg3"/>
            <div className={`pr-2 flex flex-col gap-5 items-stretch h-full overflow-auto ${classNameScrollbarThin}`}>
                {filter}
            </div>
        </div>}
        <div className={`grow flex min-w-0 flex-col gap-3 ${classNameMain} overflow-x-auto overflow-y-hidden`}>
            {(topTitle || top) &&
                <div className={`shrink-0 flex items-center gap-4`}>
                    {topTitle && <Title className="self-start grow text-xl">{topTitle}</Title>}
                    {top}
                </div>}
            {topDivider && <Divider classNameColor="bg-bg3"/>}
            {main}
        </div>
    </DashCard>;

/*
* you can let this component handle you state or handle it outside and pass on*/
export function AccordionDiv({isExpanded = null, onSetExpanded = undefined, summary = undefined, children = undefined, classNameDiv = "",
    classNameWrapper = undefined, classNameSummary = undefined, classNameMain = undefined, arrow = false,
    handle = false, handleColor = undefined,
    scroll = false, className = "", initiallyOpen = false, collapseAll = undefined, dataTest = ""}) {
    const [privateIsShown, setPrivateIsShown] = useState(initiallyOpen);
    const isShown = isExpanded !== null ? isExpanded : privateIsShown;

    useEffect(() => {
        collapseAll !== undefined && setPrivateIsShown(!collapseAll);
    }, [collapseAll]);

    return <div className={`flex items-stretch ${scroll && "overflow-y-auto"} ${classNameWrapper}`} data-test={dataTest + "-accordion-open"}>
        {handle && <div className={`w-[0.575rem] min-w-0 shrink-0 rounded-l-md ${handleColor || "bg-handle"}`}/>}
        <DashCard className={tailCheck(className, "flex flex-col", "grow", "shadow-sm", handle && "rounded-none rounded-r-md")}>
            <div
                className={tailCheck(classNameSummary, "flex", "items-center", "cursor-pointer", "gap-2", "w-full", "justify-between")}
                onClick={e => {
                    e.stopPropagation();
                    isExpanded !== null ? onSetExpanded?.(!isExpanded) : setPrivateIsShown(prevState => !prevState);
                }}>
                {summary}
                {arrow && <div className={`pl-3 py-2 text-icon cursor-pointer`}>
                    <AnimatePickerRotation className={`${isShown && "rotate-180"} text-title font-bolder`}/>
                </div>}
            </div>
            <ToggleDiv className={tailCheck(classNameMain, scroll && "overflow-y-auto", classNameScrollbar)} show={isShown}>
                <div className={tailCheck(classNameDiv, "mt-4", "flex flex-col", "gap-2.5")}>
                    {children}
                </div>
            </ToggleDiv>
        </DashCard>
    </div>;
}


export function ReadMoreList({className, classNameMore, children, numToShow = 3}) {
    const [readMore, setReadMore] = useState(false);
    const isOverflow = children.length > numToShow;

    return <div className={className}>
        {children.slice(0, !readMore ? numToShow : children.length).map((c, i) => c)}
        {isOverflow &&
            <span
                onClick={ e => (e.stopPropagation(), setReadMore(prevState => !prevState))}
                className={`text-prm-light cursor-pointer ${classNameMore}`}>
                See {!readMore ? "more" : "less"}
            </span>}
    </div>;
}

export function ReadMoreParagraph({children, width = undefined, linesToShow = 4, className = "", dataTest="read-more-paragraph", classNameMore =""}) {
    const [readMore, setReadMore] = useState(false);
    const [isOverflow, setIsOverflow] = useState(false);
    const pRef = useRef();

    useEffect(() => {
        pRef?.current?.scrollHeight > pRef?.current?.clientHeight && setIsOverflow(true);
    }, []);

    const onClick = e => isOverflow && (e.stopPropagation(), setReadMore(prevState => !prevState));

    return <div className={`relative ${isOverflow && "mb-3"}`} data-test={dataTest}>
        <div
            ref={pRef} onClick={onClick}
            {...(readMore || {
                style: {
                    maxWidth: width,
                    WebkitLineClamp: linesToShow,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical"
                }
            })}
            className={`text-ellipsis overflow-hidden whitespace-pre-wrap ${isOverflow && "cursor-pointer"} ${className}`}>
            {children}
        </div>
        {isOverflow &&
            <span
                onClick={onClick}
                className={`${classNameMore} text-prm-light absolute bottom-0 translate-y-full cursor-pointer`}>
                Read {!readMore ? "more" : "less"}
            </span>}
    </div>;
}

export function ReadMoreWrapper({arr, elementRender, numToShow = 3}) {
    const [readMore, setReadMore] = useState(false);

    return <div>
        {arr.slice(0, readMore ? arr.length : numToShow).map((a, i) => elementRender(a, i))}
        {arr.length > numToShow && <Title onClick={() => setReadMore((prevVal) => !prevVal)} className="cursor-pointer" classNameColor="text-prm-main">
            {readMore ? "read less" : `${arr.length - numToShow} more... >`}
        </Title>}
    </div>;
}

export function UIFadeContent({children, fadeDep, className, ...rest}) {
    const vals = useRef({children}).current;
    const [isVisible, setVisibility] = useState(false);

    const fadeDependency = fadeDep !== undefined ? fadeDep : children;

    useEffect(() => {
        setVisibility(false);

        const tmChangeChildren = setTimeout(() => {
            vals.children = children;
            setVisibility(true);
        }, 200);

        return () => clearTimeout(tmChangeChildren);
    }, [fadeDependency]);

    return <div {...rest} className={`transition-opacity ${isVisible ? "opacity-100" : "opacity-50"} ${className}`}>
        {vals.children}
    </div>;
}


export const colorWheel = ["#cce1ff", "#93b4fd", "#757ccc", "#537dee", "#17adcc", "#0090d9", "#0079be", "#0e4380", "#696F7F"];

export const LegendBar = ({legends, className}) => <div className="flex items-center">
    {legends.map((def, i) => <div
        style={{background: def.c || colorWheel[i]}} {...(def.tip && genTooltip(def.tip))}
        className={`${def.c} last:rounded-r-lg min-w-0 first:rounded-l-lg text-prm-contrast ${tailCheck(className, "py-1", "px-2", "font-semibold")}`}
        key={i}>{def?.h} </div>)}
</div>;


export const DataListInfo = ({infoDefs, classNameH = "w-32", className = undefined, colNum = 3, gridTemplateColumns = undefined, fadeDep = undefined, depth = 0, flattenLevel}) => {
    const isFlattened = flattenLevel === undefined || depth < flattenLevel;

    return <div
        style={{gridTemplateColumns: gridTemplateColumns || `min-content repeat(${colNum - 1}, minmax(0, 1fr))`}}
        className={`${className} grid grid-flow-row gap-4`}>
        {(Array.isArray(infoDefs) ? infoDefs : [infoDefs]).map(({h, k, divider, classNameH: clsH} = {}, i, arr) => {
            const isTitle = k?.h || k?.[0]?.h;
            const next = arr[i + 1];
            const nextStartsLine = next?.k?.h || next?.k?.[0]?.h || next?.divider;
            return  (!!h || divider) && (k || k === false || k === 0) && <Fragment key={i}>
                {(isTitle || divider) && <Divider style={{gridColumn: `span ${colNum} / span ${colNum}`}}/>}
                {!divider && <>
                    <div
                        data-test={`${h.toLowerCase().toKebabCase()}-data-list-info-title`}
                        className={`${clsH} ${(!isTitle || !isFlattened) ? classNameH : ""}  ${isTitle ? "text-md" : "text-xs"}`}
                        style={{...isTitle && isFlattened && {gridColumn: `span ${colNum} / span ${colNum}`}}}>
                        {h}{isTitle && ":"}
                    </div>
                    {isTitle ? <>
                        <div style={{overflowWrap: "break-word", gridColumn: isFlattened ? `1 / -1` : `2 / -1`}}>
                            <DataListInfo {...{classNameH, flattenLevel}} colNum={isFlattened ? colNum : colNum - 1} depth={depth + 1} infoDefs={k}/>
                        </div>
                        {next && !nextStartsLine && <Divider style={{gridColumn: `span ${colNum} / span ${colNum}`}}/>}
                    </>
                        : <UIFadeContent
                            data-test={`${h.toLowerCase().toKebabCase()}-data-list-info-value`}
                            style={{overflowWrap: "break-word", gridColumn: `span ${colNum - 1} / span ${colNum - 1}`}}
                            className={`text-xs text-desc`} fadeDep={fadeDep}>
                            {k?.[0]?.[0]?.h ? k?.map((v, i) =>
                                <div key={i} className="py-1 flex flex-col gap-1">
                                    <DataListInfo {...{classNameH, flattenLevel}} colNum={colNum - 1} depth={depth + 1} infoDefs={v}/>
                                    <Divider/>
                                </div>)
                                : Array.isArray(k) ? k.map((v, i) => v && typeof v !== "object" &&
                                        <Fragment key={i}>
                                            <div/>
                                            <ReadMoreParagraph className=" max-w-full">{v}</ReadMoreParagraph>
                                        </Fragment>).filter(Boolean)
                                    : <ReadMoreParagraph className=" max-w-full">{isValidElement(k) ? k : String(k)}</ReadMoreParagraph>}
                        </UIFadeContent>}
                </>}
            </Fragment>;
        })}
    </div>;
};


export const SidePanelItemValue = ({title, value}) =>
    <div data-test={`${title?.toLowerCase().toKebabCase()}-side-panel-item`} className="flex flex-col gap-1">
        <Title data-test="side-panel-item-title" classNameColor="text-tx-steel">{title}</Title>
        <div data-test="side-panel-item-value" className={`ml-4 whitespace-pre-wrap ${!value && "text-tx-steel"}`}>{value || "(none)"}</div>
    </div>;


export const expiryDurationDefs = {
        "forever": {h: "Forever", val: null},
        "30d": {h: "30 days", val: () => modDate("", d => (d.setDate(d.getDate() + 30), d.setHours(0, 0, 0, 0)))},
        "60d": {h: "60 days", val: () => modDate("", d => (d.setDate(d.getDate() + 60), d.setHours(0, 0, 0, 0)))},
        "90d": {h: "90 days", val: () => modDate("", d => (d.setDate(d.getDate() + 90), d.setHours(0, 0, 0, 0)))},
        "pickDate": {h: "Pick expiration date", val: inp => modDate(inp.expiration, d => d.setHours(0, 0, 0, 0))},
    }, expiryDurationOpts = objToArrK(expiryDurationDefs);

export function PickExpiryDate({inp, onChange}) {
    return (<>
        <FormLabel label="Mute Duration" classNameCol1="w-32" classNameCol2="w-full flex items-center gap-2">
            <UIComboBox
                selectMode field="muteDuration" onChange={onChange} className="w-48"
                value={inp.muteDuration}
                opts={expiryDurationOpts}/>
        </FormLabel>
        {inp.muteDuration === "pickDate" &&
            <TextInput
                type="date" onChange={onChange} value={inp.expiration} field="expiration"
                min={timePickerFrmt(modDate("", d => d.setDate(d.getDate()+1)))}
                className="dark:[color-scheme:dark] w-40 ml-32"/>}
    </>);

}

export function PinToggle({pinnedItems, tglPinned, id, dataTest}) {
    return <div
        className={`icon-pin active:scale-105 cursor-pointer text-lg hover:opacity-70 transition-opacity
            ${!pinnedItems.has(id)&& "opacity-20 hover:opacity-10"}`} data-topic-code={id} data-test={dataTest}
        onClick={e => {
            e.stopPropagation();
            tglPinned(id);
        }}/>;
}


export const UIHighlightedTitle = ({
    text,
    highlight,
    highlightClassName,
}) => {
    if (!text) return null;
    if (!highlight) return <span>{text}</span>;

    function escapeRegExp(str) {
        return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }

    const safeHighlight = escapeRegExp(highlight);
    const regex = new RegExp(`(${safeHighlight})`, "gi");

    const parts = text.split(regex);

    return (
        <>
            {parts.map((part, index) => {
                const isMatch = part.toLowerCase() === highlight.toLowerCase();

                return (
                    <span key={index}>
                        {isMatch ? (
                            <span className={highlightClassName}>{part}</span>
                        ) : (
                            part
                        )}
                    </span>
                );
            })}
        </>
    );
};
